'use client';
import { Button as HeroUiButton, ButtonProps as HeroUiButtonProps } from '@heroui/button';
import { extendVariants } from '@heroui/react';
import type { FC } from 'react';
import { heroUiTheme, ThemeName, themeConfigs } from '@/hero';

const ExtendedButton = extendVariants(HeroUiButton, {
  variants: {
    color: {
      white: 'bg-white data-[hover=true]:bg-background text-foreground',
    },
  },
});

export type ButtonProps = Omit<HeroUiButtonProps, 'color'> & {
  color?: HeroUiButtonProps['color'] | 'white';
  noBackground?: boolean;
  href?: string;
};

type ColorPalette = {
  gradient?: string;
  [key: string]: unknown;
};

const getColorPalette = (
  color: string | undefined,
  themeName?: ThemeName,
): ColorPalette | undefined => {
  const colors = themeName ? themeConfigs[themeName]?.colors : heroUiTheme.themes?.light?.colors;
  if (!colors) return undefined;
  const key = color === undefined || color === 'default' ? 'default' : color;
  const palette = (colors as Record<string, unknown>)[key];
  return typeof palette === 'object' && palette !== null ? (palette as ColorPalette) : undefined;
};

const getBackground = (
  noBackground: boolean,
  variant: string | undefined,
  color: string | undefined,
  themeName?: ThemeName,
) => {
  const colorPalette = getColorPalette(color, themeName);
  if (noBackground || colorPalette?.gradient === undefined) return undefined;
  if (variant === 'solid' || variant === undefined || variant === 'shadow')
    return { background: colorPalette?.gradient };
  return undefined;
};

export const Button = ({ noBackground = false, ...props }: ButtonProps) => {
  // `extendVariants` does not infer custom color values on the extended component.
  const Component = ExtendedButton as unknown as FC<ButtonProps>;

  return (
    <Component
      {...props}
      style={{
        ...getBackground(noBackground, props.variant, props.color),
        ...props.style,
      }}
    >
      {props.children}
    </Component>
  );
};
